import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { PriceCardDeck } from 'components/price-card';
import { PriceCurvePreviewChart } from 'components/price-curve-chart/price-curve-preview-chart';
import {
  getVisibleSliceBounds,
  type HistoricRange,
} from 'components/price-curve-chart/types';
import moment from 'moment';
import { formatResource } from 'utils/misc';
import request from 'lib/request';
import routes from '~/routes';
import {
  METHOD_FLAT,
  METHOD_MODIFIED_STRIP,
  METHOD_CUSTOM_UPLOAD,
  FALLBACK_DISCRETE_PRICE,
} from '../types';
import {
  METHOD_TYPE_LABELS,
  STRIP_DATE_MODE_LABELS,
  FALLBACK_TYPE_LABELS,
  buildChartData,
  buildPriceCards,
  formatCurrency,
  priceDeckDetailToFormValues,
} from '../utils';
import { PriceDeckScheduleTable } from '../price-deck-schedule-table';
import {
  previewPriceDeckLoadableAtom,
  stripReferenceLoadableAtom,
} from '../price-deck-form/atoms';
import { usePriceDeckDetailAtom } from './atoms';
import styles from './page.module.scss';

const ShowPriceDeckPage: FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const asOf = new URLSearchParams(location.search).get('as_of');
  const isHistorical = !!asOf;

  const resource = useAtomValue(usePriceDeckDetailAtom(id, asOf));
  const { loading, error, data } = formatResource(resource);
  const deck = data?.data;

  // Preview via shared atom
  const [previewState, triggerPreview] = useAtom(previewPriceDeckLoadableAtom);
  const preview = previewState.state === 'hasData' ? previewState.data : null;
  const previewError =
    previewState.state === 'hasError'
      ? (previewState.error as Error).message
      : null;

  // Strip reference for modified strip dotted lines
  const [stripRefState, triggerStripRef] = useAtom(stripReferenceLoadableAtom);
  const stripReference =
    stripRefState.state === 'hasData' ? stripRefState.data : null;

  // Historic data visibility — backend returns full history for strip methods;
  // radio buttons just control the x-axis view.
  const [historicMonths, setHistoricMonths] = useState<HistoricRange>(0);
  const [visibleXMin, setVisibleXMin] = useState<number | undefined>();
  const [visibleXMax, setVisibleXMax] = useState<number | undefined>();

  const handleVisibleRangeChange = useCallback(
    (xMin: number | undefined, xMax: number | undefined) => {
      setVisibleXMin(xMin);
      setVisibleXMax(xMax);
    },
    []
  );

  const isStripMethod =
    deck?.methodType === 'strip' || deck?.methodType === 'modified_strip';

  // Fetch preview once deck is loaded
  useEffect(() => {
    if (!deck) return;
    const formValues = priceDeckDetailToFormValues(deck);
    triggerPreview(formValues);
    if (deck.methodType === 'modified_strip') {
      triggerStripRef(formValues);
    }
  }, [deck, triggerPreview, triggerStripRef]);

  const handleBack = useCallback(() => {
    history.push(routes.priceDecksPath);
  }, [history]);

  const handleDownloadFile = useCallback(async () => {
    if (!id) return;
    const response = await request.get<{ download_url: string }>(
      `/price_decks/${id}/download`
    );
    if ('error' in response) return;
    window.open(response.download_url, '_blank', 'noopener,noreferrer');
  }, [id]);

  // Configuration items for the detail panel
  const configItems = useMemo(() => {
    if (!deck) return [];
    const items: { label: string; value: string }[] = [];
    const cfg = deck.config;

    if (deck.description) {
      items.push({ label: 'Description', value: deck.description });
    }
    items.push({
      label: 'Pricing Method',
      value: METHOD_TYPE_LABELS[deck.methodType] ?? deck.methodType,
    });

    if (
      deck.methodType !== METHOD_FLAT &&
      deck.methodType !== METHOD_CUSTOM_UPLOAD
    ) {
      if (deck.stripPriceDate) {
        items.push({ label: 'Strip Date', value: deck.stripPriceDate });
      } else if (deck.stripDateMode) {
        items.push({
          label: 'Strip Date',
          value:
            STRIP_DATE_MODE_LABELS[deck.stripDateMode] ?? deck.stripDateMode,
        });
      }
    }

    if (preview?.rows?.length) {
      items.push({
        label: 'Total Duration',
        value: `${preview.rows.length} months`,
      });
    }

    if (deck.methodType === METHOD_MODIFIED_STRIP) {
      if (cfg.oil_strip_duration)
        items.push({
          label: 'Oil Strip Duration',
          value: `${cfg.oil_strip_duration} months`,
        });
      if (cfg.oil_scale_factor !== null)
        items.push({
          label: 'Oil Scale Factor',
          value: `${cfg.oil_scale_factor}%`,
        });
      if (cfg.oil_fallback_type)
        items.push({
          label: 'Oil Fallback',
          value:
            FALLBACK_TYPE_LABELS[cfg.oil_fallback_type] ??
            cfg.oil_fallback_type,
        });
      if (
        cfg.oil_fallback_type === FALLBACK_DISCRETE_PRICE &&
        cfg.oil_flat_price !== null
      ) {
        items.push({
          label: '$/BBL',
          value: formatCurrency(cfg.oil_flat_price, ''),
        });
      }
      if (cfg.gas_strip_duration)
        items.push({
          label: 'Gas Strip Duration',
          value: `${cfg.gas_strip_duration} months`,
        });
      if (cfg.gas_scale_factor !== null)
        items.push({
          label: 'Gas Scale Factor',
          value: `${cfg.gas_scale_factor}%`,
        });
      if (cfg.gas_fallback_type)
        items.push({
          label: 'Gas Fallback',
          value:
            FALLBACK_TYPE_LABELS[cfg.gas_fallback_type] ??
            cfg.gas_fallback_type,
        });
      if (
        cfg.gas_fallback_type === FALLBACK_DISCRETE_PRICE &&
        cfg.gas_flat_price !== null
      ) {
        items.push({
          label: '$/MMBTU',
          value: formatCurrency(cfg.gas_flat_price, ''),
        });
      }
    }

    if (deck.methodType === METHOD_FLAT) {
      if (cfg.oil_flat_price !== null)
        items.push({
          label: 'Oil Price',
          value: formatCurrency(cfg.oil_flat_price, '$/BBL'),
        });
      if (cfg.gas_flat_price !== null)
        items.push({
          label: 'Gas Price',
          value: formatCurrency(cfg.gas_flat_price, '$/MMBTU'),
        });
    }

    if (deck.methodType === METHOD_CUSTOM_UPLOAD) {
      if (cfg.deck_type)
        items.push({
          label: 'Deck Type',
          value: cfg.deck_type === 'relative' ? 'Relative' : 'Calendar Dates',
        });
      if (cfg.anchor_date)
        items.push({ label: 'Anchor Date', value: cfg.anchor_date });
      if (cfg.oil_fallback_type)
        items.push({
          label: 'Oil Fallback',
          value:
            FALLBACK_TYPE_LABELS[cfg.oil_fallback_type] ??
            cfg.oil_fallback_type,
        });
      if (
        cfg.oil_fallback_type === FALLBACK_DISCRETE_PRICE &&
        cfg.oil_flat_price !== null
      ) {
        items.push({
          label: '$/BBL',
          value: formatCurrency(cfg.oil_flat_price, ''),
        });
      }
      if (cfg.gas_fallback_type)
        items.push({
          label: 'Gas Fallback',
          value:
            FALLBACK_TYPE_LABELS[cfg.gas_fallback_type] ??
            cfg.gas_fallback_type,
        });
      if (
        cfg.gas_fallback_type === FALLBACK_DISCRETE_PRICE &&
        cfg.gas_flat_price !== null
      ) {
        items.push({
          label: '$/MMBTU',
          value: formatCurrency(cfg.gas_flat_price, ''),
        });
      }
    }

    return items;
  }, [deck, preview]);

  // Price cards from preview stats
  const priceCards = useMemo(() => buildPriceCards(preview?.stats), [preview]);

  // Chart series from preview rows
  const { chartSeries, chartDates } = useMemo(
    () =>
      buildChartData(
        preview,
        deck?.methodType === 'modified_strip' ? stripReference : null
      ),
    [preview, stripReference, deck?.methodType]
  );

  const filteredPreviewRows = useMemo(() => {
    if (!preview?.rows?.length || !chartDates.length)
      return preview?.rows ?? [];

    const bounds = getVisibleSliceBounds(chartDates, visibleXMin, visibleXMax);
    if (!bounds) return preview.rows;

    return preview.rows.slice(bounds.start, bounds.end);
  }, [preview, chartDates, visibleXMin, visibleXMax]);

  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Unable to load price deck"
        subtitle={error}
      />
    );
  }

  if (loading && !deck) {
    return (
      <>
        <PageHeader breadcrumbs={[]} title="Price Deck" actions={<></>} />
        <InlineLoading description="Loading price deck..." />
      </>
    );
  }

  if (!deck) return null;

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title={
          isHistorical
            ? `${deck.name} (as of ${moment
                .utc(asOf)
                .format('M/D/YYYY h:mm A')})`
            : deck.name
        }
        actions={
          <>
            <Button kind="ghost" onClick={handleBack}>
              Back
            </Button>
            {!isHistorical && (
              <Button
                kind="primary"
                renderIcon={Edit}
                onClick={() =>
                  history.push(routes.priceDeckEditPath.replace(':id', id))
                }
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      <div className={styles.page}>
        <section className={styles.configPanel}>
          <h3 className={styles.configTitle}>Price Deck Configurations</h3>
          <div className={styles.configGrid}>
            {configItems.map((item) => (
              <div key={item.label} className={styles.configItem}>
                <span className={styles.configLabel}>{item.label}</span>
                <span className={styles.configValue}>{item.value}</span>
              </div>
            ))}
            {deck.methodType === METHOD_CUSTOM_UPLOAD &&
              deck.config.original_filename && (
                <div className={styles.configItem}>
                  <span className={styles.configLabel}>Uploaded File</span>
                  <Button
                    kind="ghost"
                    size="sm"
                    className={styles.configLink}
                    onClick={handleDownloadFile}
                  >
                    {deck.config.original_filename}
                  </Button>
                </div>
              )}
          </div>
        </section>

        {previewError && (
          <InlineNotification
            kind="warning"
            title="No data available"
            subtitle={previewError}
            hideCloseButton
          />
        )}

        {preview && (
          <div className={styles.content}>
            {priceCards.length > 0 && (
              <section className={styles.sectionPanel}>
                <h3 className={styles.sectionTitle}>Current Prices</h3>
                <PriceCardDeck items={priceCards} />
              </section>
            )}

            {chartSeries && (
              <section className={styles.whitePanel}>
                <h3 className={styles.sectionTitle}>Price Curve Preview</h3>
                <PriceCurvePreviewChart
                  series={chartSeries}
                  dates={chartDates}
                  filterMode="projection"
                  showHistoricControls={isStripMethod}
                  historicRange={historicMonths}
                  onHistoricRangeChange={setHistoricMonths}
                  onVisibleRangeChange={handleVisibleRangeChange}
                  todayLineTs={
                    isStripMethod
                      ? moment.utc().startOf('month').valueOf()
                      : undefined
                  }
                />
              </section>
            )}

            {filteredPreviewRows.length > 0 && (
              <section className={styles.whitePanel}>
                <h3 className={styles.sectionTitle}>Price Schedule</h3>
                <PriceDeckScheduleTable
                  key={filteredPreviewRows.length}
                  rows={filteredPreviewRows}
                />
              </section>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export { ShowPriceDeckPage };
